import clsx from 'clsx'
import { html, PropertyValueMap, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { translate } from 'lit-i18n'

import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import DapDSIcon from '../icon/icon.component'
import styles from './spinner.scss?inline'

const SpinnerBaseWithSized = SizedMixin(DdsElement)

export type SpinnerVariant =
  | 'neutral'
  | 'brand'
  | 'negative'
  | 'positive'
  | 'inverted'

export type SpinnerOrientation = 'vertical' | 'horizontal'

/**
 * `dap-ds-spinner`
 * @summary Loading spinner component.
 * @element dap-ds-spinner
 * @title - Spinner
 *
 * @csspart base - The main spinner container.
 * @csspart icon - The loading icon element.
 * @csspart icon-base - The base of the loading icon.
 * @csspart icon-content - The content of the loading icon.
 * @csspart text - The loading text element.
 *
 * @slot - The loading text content.
 * @slot icon - The loading icon content.
 *
 * @cssprop --dds-spinner-icon-color-neutral - Color for neutral spinner icons (default: var(--dds-icon-neutral-base))
 * @cssprop --dds-spinner-icon-color-brand - Color for brand spinner icons (default: var(--dds-icon-brand-subtle))
 * @cssprop --dds-spinner-icon-color-negative - Color for negative spinner icons (default: var(--dds-icon-negative-subtle))
 * @cssprop --dds-spinner-icon-color-positive - Color for positive spinner icons (default: var(--dds-icon-positive-subtle))
 * @cssprop --dds-spinner-icon-color-inverted - Color for inverted spinner icons (default: var(--dds-icon-neutral-inverted))
 * @cssprop --dds-spinner-text-spacing - Spacing between spinner and text (default: var(--dds-spacing-400))
 * @cssprop --dds-spinner-animation-duration - Duration of the spinner animation (default: 1s)
 *
 * @property { 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' } size - The size of the spinner. Default is `lg`. See SizedMixin.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg"); see SizedMixin.
 */
export default class DapDSSpinner extends SpinnerBaseWithSized {
  static tagName = 'dap-ds-spinner'

  static dependencies = {
    'dap-ds-icon': DapDSIcon,
  }

  constructor() {
    super()
    this.size = 'lg'
  }
  /**
   * The variant of the spinner.
   * @type {"neutral" | "brand" | "negative" | "positive" | "inverted"}
   */
  @property({ type: String, reflect: true }) variant: SpinnerVariant = 'neutral'

  /** The size of the spinner in pixels. This overrides the size attribute */
  @property({ type: Number }) staticSize?: number
  /** The loading text. */
  @property({ type: String }) text: string
  /** Removes the text color */
  @property({ type: Boolean, reflect: true }) noColor = false
  /**
   * The orientation of the spinner (icon and text layout).
   * @type {"vertical" | "horizontal"}
   */
  @property({ type: String, reflect: true }) orientation: SpinnerOrientation =
    'vertical'

  /**
   * The aria-live politeness level for screen readers.
   * @type {"polite" | "assertive" | "off"}
   */
  @property({ type: String, attribute: 'aria-live' })
  ariaLive: 'polite' | 'assertive' | 'off' = 'polite'
  /**
   * Delay in milliseconds before showing the spinner.
   * Prevents flash for quick operations.
   */
  @property({ type: Number }) delay = 0

  /** @internal */
  @state() private _visible = false

  private _delayTimeout?: number

  static readonly styles = unsafeCSS(styles)

  /** Maps effectiveSize from SizedMixin to spinner visual size (xxs→xs, else effectiveSize). */
  private get spinnerSize(): 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' {
    return this.effectiveSize === 'xxs' ? 'xs' : this.effectiveSize
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.delay > 0) {
      this._delayTimeout = window.setTimeout(() => {
        this._visible = true
      }, this.delay)
    } else {
      this._visible = true
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._delayTimeout) {
      clearTimeout(this._delayTimeout)
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this._setupIconSlot()
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)
    // Update slotted icons when size, staticSize, or customStyles changes
    if (
      _changedProperties.has('size') ||
      _changedProperties.has('sizeMap') ||
      _changedProperties.has('staticSize') ||
      _changedProperties.has('customStyles')
    ) {
      this._updateSlottedIcons()
    }
  }

  private _setupIconSlot(): void {
    // Initial update in case icons are already slotted
    this._updateSlottedIcons()
  }

  private _updateSlottedIcons(): void {
    try {
      if (!this.shadowRoot) return

      const iconSlot = this.shadowRoot.querySelector(
        'slot[name="icon"]',
      ) as HTMLSlotElement

      if (!iconSlot) return

      const assignedElements = iconSlot.assignedElements({
        flatten: true,
      })

      // If custom styles are provided, don't apply inline animations
      // Let the custom CSS handle the animation instead
      const hasCustomStyles = !!this.customStyles

      const animationDuration =
        getComputedStyle(this).getPropertyValue(
          '--dds-spinner-animation-duration',
        ) || '1s'

      assignedElements.forEach(element => {
        if (element instanceof HTMLElement) {
          const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
          ).matches

          // If it's an icon component directly slotted, apply animation and size
          if (
            element.tagName === 'DAP-DS-ICON' ||
            element.localName === 'dap-ds-icon'
          ) {
            // Only apply animation via inline styles if no custom styles are provided
            // Custom styles should handle animation in that case
            if (!hasCustomStyles) {
              if (prefersReducedMotion) {
                element.style.animation = `dds-fade ${animationDuration} ease-in-out infinite`
              } else {
                element.style.animation = `dds-spin ${animationDuration} linear infinite`
              }
            } else {
              // Clear any existing inline animation to let CSS take over
              element.style.animation = ''
            }
            element.style.display = 'block'

            if (this.spinnerSize) {
              element.setAttribute('size', this.spinnerSize)
            }
            if (this.staticSize !== undefined) {
              element.setAttribute('staticSize', this.staticSize.toString())
            }
          } else {
            // If it's a wrapper element (like span), apply animation only to the wrapper
            // The nested icon will rotate with the wrapper, so we don't need to animate it separately
            if (!hasCustomStyles) {
              if (prefersReducedMotion) {
                element.style.animation = `dds-fade ${animationDuration} ease-in-out infinite`
              } else {
                element.style.animation = `dds-spin ${animationDuration} linear infinite`
              }
            } else {
              // Clear any existing inline animation to let CSS take over
              element.style.animation = ''
            }
            element.style.display = 'block'

            // Update nested icon size attributes but don't apply animation to it
            const nestedIcon = element.querySelector('dap-ds-icon')
            if (nestedIcon) {
              nestedIcon.style.display = 'block'
              // Remove any existing animation from nested icon to prevent double animation
              nestedIcon.style.animation = ''

              if (this.spinnerSize) {
                nestedIcon.setAttribute('size', this.spinnerSize)
              }
              if (this.staticSize !== undefined) {
                nestedIcon.setAttribute(
                  'staticSize',
                  this.staticSize.toString(),
                )
              }
            }
          }
        }
      })
    } catch (error) {
      console.warn('Failed to update slotted icons:', error)
    }
  }

  render() {
    if (!this._visible) {
      return null
    }

    return html`
      <div
        part="base"
        role="status"
        name="icon-spinner"
        aria-label=${this.ariaLabel ?? translate('spinner')}
        aria-live=${this.ariaLive}
        class="${clsx('spinner', {
          [`spinner--${this.spinnerSize}`]: this.spinnerSize,
          [`spinner--${this.variant}`]: this.variant,
          [`spinner--${this.orientation}`]: this.orientation,
        })}">
        <slot name="icon" @slotchange=${this._updateSlottedIcons}>
          <dap-ds-icon
            part="icon"
            exportparts="base: icon-base, content: icon-content"
            size=${this.spinnerSize}
            staticSize=${ifDefined(this.staticSize)}
            name="loading-spinner"
            class="spinner__icon"></dap-ds-icon>
        </slot>
        ${this.text
          ? html` <span
              part="text"
              class=${clsx('spinner__text', {
                'spinner__text--no-color': this.noColor,
              })}>
              ${this.text}
            </span>`
          : html`<slot></slot>`}
      </div>
    `
  }
}
